import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import {
  Box, IconButton, CircularProgress, Typography, Modal,
  useMediaQuery, Snackbar, Alert, Button, LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useApi } from "../api/ApiContext";
import { motion, AnimatePresence } from "framer-motion";

function getFileUrl(p) {
  if (!p) return "";
  return p.startsWith("http") ? p : `https://ksiega.kalmarry.pl/${p.replace(/^\/+/, "")}`;
}
// Derive thumbnail URL from image_path: stored as uploads/thumbs/[basename].jpg
function getThumbnailUrl(p) {
  if (!p) return "";
  const base = p.split("/").pop().replace(/\.[^.]+$/, "");
  return `https://ksiega.kalmarry.pl/uploads/thumbs/${base}.jpg`;
}
function isVideoFile(p) { return /\.(mp4|webm|mov|avi|mkv|m4v)$/i.test(p || ""); }
function getFileExt(p) { const m = (p || "").match(/\.(\w+)(?:\?.*)?$/); return m ? m[1].toLowerCase() : "jpg"; }
async function downloadBlob(url, filename) {
  const resp = await fetch(url, { mode: "cors" });
  const blob = await resp.blob();
  const a = Object.assign(document.createElement("a"), { href: window.URL.createObjectURL(blob), download: filename });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  window.URL.revokeObjectURL(a.href);
}

const slideVariants = {
  enter: (d) => ({ y: d > 0 ? 500 : -500, opacity: 0 }),
  center: { y: 0, opacity: 1, transition: { duration: 0.28 } },
  exit: (d) => ({ y: d > 0 ? -500 : 500, opacity: 0, transition: { duration: 0.14 } }),
};

// ─── Post Card (mobile feed) ──────────────────────────────────────────────────
// memo: only re-renders when its own props change, not when Gallery favorites/toast state changes
const PostCard = memo(function PostCard({ img, isLiked, onLike, onOpen, onDownload }) {
  const lastTap = useRef(0);
  const tapTimer = useRef(null);
  const [heartPos, setHeartPos] = useState(null);
  const isVid = isVideoFile(img.image_path);
  const thumbUrl = getThumbnailUrl(img.image_path);

  const handleMediaClick = useCallback((e) => {
    const now = Date.now();
    if (now - lastTap.current < 320 && tapTimer.current) {
      clearTimeout(tapTimer.current);
      tapTimer.current = null;
      const rect = e.currentTarget.getBoundingClientRect();
      const cx = (e.changedTouches?.[0]?.clientX ?? e.clientX) - rect.left;
      const cy = (e.changedTouches?.[0]?.clientY ?? e.clientY) - rect.top;
      setHeartPos({ x: cx, y: cy });
      setTimeout(() => setHeartPos(null), 900);
      if (!isLiked) onLike(img);
    } else {
      tapTimer.current = setTimeout(() => { tapTimer.current = null; onOpen(); }, 300);
    }
    lastTap.current = now;
  }, [isLiked, onLike, onOpen, img]);

  return (
    // contentVisibility:auto — browser skips layout+paint for off-screen cards entirely
    // containIntrinsicSize reserves ~500px so the scrollbar thumb stays accurate
    <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #efefef", contentVisibility: "auto", containIntrinsicSize: "0 500px" }}>
      {/* Media */}
      <Box
        sx={{ width: "100%", aspectRatio: "1/1", bgcolor: "#111", position: "relative", overflow: "hidden", cursor: "pointer" }}
        onClick={handleMediaClick}
      >
        {isVid ? (
          <>
            {/* Show thumbnail image for video; falls back to video element if no thumbnail */}
            <img src={thumbUrl} alt="" draggable={false} loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              onError={e => {
                // No thumbnail yet — swap to video element
                const v = document.createElement("video");
                v.src = `${img.url}#t=0.001`; v.preload = "metadata";
                v.muted = true; v.playsInline = true;
                Object.assign(v.style, { width: "100%", height: "100%", objectFit: "cover" });
                e.target.replaceWith(v);
              }}
            />
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
              <Box sx={{ bgcolor: "rgba(0,0,0,0.45)", borderRadius: "50%", p: 1.2, backdropFilter: "blur(4px)" }}>
                <PlayArrowIcon sx={{ color: "#fff", fontSize: 36, display: "block" }} />
              </Box>
            </Box>
          </>
        ) : (
          <img src={thumbUrl} alt={img.text || ""} draggable={false} loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={e => { e.target.src = img.url; }}
          />
        )}

        <AnimatePresence>
          {heartPos && (
            <motion.div
              initial={{ scale: 0, opacity: 1 }}
              animate={{ scale: 1.2, opacity: 1 }}
              exit={{ scale: 1.6, opacity: 0 }}
              transition={{ duration: 0.45 }}
              style={{
                position: "absolute", left: heartPos.x, top: heartPos.y,
                transform: "translate(-50%, -50%)", pointerEvents: "none",
                filter: "drop-shadow(0 4px 16px rgba(235,87,138,0.7))",
              }}
            >
              <FavoriteIcon sx={{ fontSize: 90, color: "#fff" }} />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Actions */}
      <Box sx={{ px: 1, pt: 0.8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={() => onLike(img)} sx={{ color: isLiked ? "#EB578A" : "#111", p: 1 }}>
            {isLiked
              ? <motion.div animate={{ scale: [1, 1.35, 1] }} transition={{ duration: 0.22 }}><FavoriteIcon /></motion.div>
              : <FavoriteBorderIcon />}
          </IconButton>
          <IconButton sx={{ color: "#111", p: 1 }} disabled>
            <ChatBubbleOutlineIcon />
          </IconButton>
          <IconButton onClick={() => onDownload(img)} sx={{ color: "#111", p: 1 }}>
            <DownloadIcon />
          </IconButton>
        </Box>
        <IconButton onClick={() => onOpen()} sx={{ color: "#111", p: 1 }}>
          <FullscreenIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {img.text && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Typography sx={{ fontSize: 14, color: "#111", lineHeight: 1.5 }}>{img.text}</Typography>
        </Box>
      )}
    </Box>
  );
});

// ─── Stories row ──────────────────────────────────────────────────────────────
function StoriesRow({ images, favorites, onOpen }) {
  return (
    <Box sx={{
      display: "flex", gap: 2, overflowX: "auto", px: 2, py: 1.5,
      borderBottom: "1px solid #efefef",
      scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" },
    }}>
      {/* "Dodaj" story */}
      <Box component={Link} to="/upload" sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, textDecoration: "none", flexShrink: 0 }}>
        <Box sx={{
          width: 62, height: 62, borderRadius: "50%",
          border: "1.5px dashed #EB578A",
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: "#fff0f5",
        }}>
          <AddCircleOutlineIcon sx={{ color: "#EB578A", fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Dodaj</Typography>
      </Box>

      {/* Photo stories */}
      {images.slice(0, 10).map((img, idx) => {
        const isLiked = favorites.includes(img.zdjecie_id);
        const thumbUrl = getThumbnailUrl(img.image_path);
        return (
          <Box key={img.zdjecie_id} onClick={() => onOpen(idx)}
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, cursor: "pointer", flexShrink: 0 }}>
            <Box sx={{
              width: 62, height: 62, borderRadius: "50%", overflow: "hidden",
              background: isLiked
                ? "linear-gradient(135deg, #EB578A, #C0145E)"
                : "linear-gradient(135deg, #ddd, #bbb)",
              p: "2.5px",
            }}>
              <Box sx={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", border: "2px solid #fff", bgcolor: "#111" }}>
                <img src={thumbUrl} alt="" loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  onError={e => { e.target.src = img.url; }}
                />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Gallery ──────────────────────────────────────────────────────────────────
const Gallery = () => {
  const { user } = useAuth();
  const api = useApi();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
  const touchStartY = useRef(null);
  const modalRef = useRef();

  const showToast = (msg, severity = "success") => setToast({ open: true, msg, severity });

  const handleEnterFullscreen = () => {
    if (modalRef.current?.requestFullscreen) modalRef.current.requestFullscreen();
    else if (modalRef.current?.webkitRequestFullscreen) modalRef.current.webkitRequestFullscreen();
  };
  const handleExitFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else if (document.webkitFullscreenElement) document.webkitExitFullscreen();
  };

  useEffect(() => {
    const fn = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", fn);
    document.addEventListener("webkitfullscreenchange", fn);
    return () => { document.removeEventListener("fullscreenchange", fn); document.removeEventListener("webkitfullscreenchange", fn); };
  }, []);

  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "";
    document.body.style.touchAction = modalOpen ? "none" : "";
    return () => { document.body.style.overflow = ""; document.body.style.touchAction = ""; };
  }, [modalOpen]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get(`/get_user_images.php?user_id=${user.id}`)
      .then(res => {
        const imgs = (res.data || []).map(img => ({ ...img, url: getFileUrl(img.image_path) }));
        setImages(imgs);
        setFavorites(imgs.filter(i => i.liked === 1).map(i => i.zdjecie_id));
      })
      .catch(() => showToast("Nie udało się załadować zdjęć.", "error"))
      .finally(() => setLoading(false));
  }, [user?.id, api]); // eslint-disable-line react-hooks/exhaustive-deps

  // useCallback keeps the same function reference across renders so React.memo on PostCard works
  const handleToggleLike = useCallback(async (img) => {
    if (!img?.zdjecie_id) return;
    const action = favorites.includes(img.zdjecie_id) ? "remove" : "add";
    await api.post("/favorite_image.php", { user_id: user.id, image_id: img.zdjecie_id, action });
    setFavorites(f => f.includes(img.zdjecie_id) ? f.filter(id => id !== img.zdjecie_id) : [...f, img.zdjecie_id]);
    showToast(action === "add" ? "Polubiono ❤️" : "Usunięto z ulubionych");
  }, [favorites, api, user?.id]); // eslint-disable-line

  const handleDownloadItem = useCallback(async (img) => {
    if (!img?.image_path) return;
    try { await downloadBlob(getFileUrl(img.image_path), `plik_${img.zdjecie_id || Date.now()}.${getFileExt(img.image_path)}`); }
    catch { showToast("Nie udało się pobrać.", "error"); }
  }, []); // eslint-disable-line

  const handleDownloadAll = async () => {
    if (!images.length || downloadingAll) return;
    setDownloadingAll(true); setDownloadProgress(0);
    let done = 0;
    for (const img of images) {
      try {
        await downloadBlob(img.url, `plik_${img.zdjecie_id || done + 1}.${getFileExt(img.image_path)}`);
        done++; setDownloadProgress(done);
        await new Promise(r => setTimeout(r, 350));
      } catch {}
    }
    setDownloadingAll(false); setDownloadProgress(0);
    showToast(`Pobrano ${done} plików 🎉`);
  };

  const openModal = useCallback((idx) => { setModalIdx(idx); setDirection(0); setModalOpen(true); }, []);
  const showPrev = () => { if (modalIdx > 0) { setDirection(-1); setModalIdx(i => i - 1); } };
  const showNext = () => { if (modalIdx < images.length - 1) { setDirection(1); setModalIdx(i => i + 1); } };

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowDown" || e.key === "ArrowRight") showNext();
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, modalIdx, images.length]); // eslint-disable-line

  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (dy > 55) showPrev(); else if (dy < -55) showNext();
    touchStartY.current = null;
  };
  const handleWheel = (e) => { if (e.deltaY > 0) showNext(); else if (e.deltaY < 0) showPrev(); };

  const currentImg = images[modalIdx];
  const isLiked = currentImg ? favorites.includes(currentImg.zdjecie_id) : false;
  const currentIsVideo = isVideoFile(currentImg?.image_path);

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: { xs: "100%", md: 935 }, mx: "auto" }}>

      {/* Stories row — mobile only */}
      {!loading && images.length > 0 && isMobile && (
        <StoriesRow images={images} favorites={favorites} onOpen={openModal} />
      )}

      {/* Feed header */}
      {images.length > 0 && !loading && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid #efefef" }}>
          <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#111" }}>
            {images.length} {images.length === 1 ? "zdjęcie" : images.length < 5 ? "zdjęcia" : "zdjęć"}
          </Typography>
          <Button
            size="small"
            startIcon={downloadingAll ? <CircularProgress size={13} sx={{ color: "#EB578A" }} /> : <DownloadForOfflineIcon />}
            onClick={handleDownloadAll}
            disabled={downloadingAll}
            sx={{
              color: "#EB578A", fontWeight: 700, fontSize: 13,
              textTransform: "none", borderRadius: 2,
              "&.Mui-disabled": { opacity: 0.55 },
            }}
          >
            {downloadingAll ? `${downloadProgress}/${images.length}` : "Pobierz wszystkie"}
          </Button>
        </Box>
      )}

      {downloadingAll && (
        <LinearProgress variant="determinate" value={(downloadProgress / images.length) * 100}
          sx={{ height: 2, bgcolor: "#ffd6e7", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#EB578A,#C0145E)" } }} />
      )}

      {/* Feed / Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress sx={{ color: "#EB578A" }} />
        </Box>
      ) : images.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
          <Box sx={{
            width: 80, height: 80, borderRadius: "50%", border: "2px solid #ddd",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Box component="span" sx={{ fontSize: 32 }}>📷</Box>
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 17, color: "#111" }}>Brak zdjęć</Typography>
          <Typography sx={{ color: "#aaa", fontSize: 14 }}>Gdy goście dodadzą zdjęcia, pojawią się tutaj</Typography>
          <Button
            component={Link} to="/upload"
            sx={{
              mt: 1, px: 3, py: 1.2, fontWeight: 700, textTransform: "none",
              background: "linear-gradient(90deg, #EB578A, #C0145E)", color: "#fff",
              borderRadius: "10px", boxShadow: "0 4px 14px rgba(235,87,138,0.35)",
            }}
          >
            Dodaj pierwsze zdjęcie
          </Button>
        </Box>
      ) : isMobile ? (
        /* Mobile: Instagram-style feed */
        images.map((img, idx) => (
          <PostCard
            key={img.zdjecie_id || idx}
            img={img}
            isLiked={favorites.includes(img.zdjecie_id)}
            onLike={handleToggleLike}
            onOpen={() => openModal(idx)}
            onDownload={handleDownloadItem}
          />
        ))
      ) : (
        /* Desktop: 3-column grid */
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "3px" }}>
          {images.map((img, idx) => {
            const isVid = isVideoFile(img.image_path);
            const isLiked = favorites.includes(img.zdjecie_id);
            return (
              <Box
                key={img.zdjecie_id || idx}
                onClick={() => openModal(idx)}
                sx={{
                  aspectRatio: "1/1", overflow: "hidden", position: "relative",
                  bgcolor: "#111", cursor: "pointer", contain: "layout style",
                  "&:hover .grid-overlay": { opacity: 1 },
                }}
              >
                {isVid ? (
                  <img src={getThumbnailUrl(img.image_path)} alt="" loading="lazy" draggable={false}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={e => {
                      const v = document.createElement("video");
                      v.src = `${img.url}#t=0.001`; v.preload = "metadata"; v.muted = true; v.playsInline = true;
                      Object.assign(v.style, { width: "100%", height: "100%", objectFit: "cover", display: "block" });
                      e.target.replaceWith(v);
                    }}
                  />
                ) : (
                  <img src={getThumbnailUrl(img.image_path)} alt="" loading="lazy" draggable={false}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={e => { e.target.src = img.url; }}
                  />
                )}
                {/* Hover overlay */}
                <Box className="grid-overlay" sx={{
                  position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.32)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 2.5,
                  opacity: 0, transition: "opacity 0.15s", pointerEvents: "none",
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, color: "#fff" }}>
                    {isLiked ? <FavoriteIcon sx={{ fontSize: 22 }} /> : <FavoriteBorderIcon sx={{ fontSize: 22 }} />}
                  </Box>
                </Box>
                {isVid && (
                  <PlayArrowIcon sx={{ position: "absolute", top: 8, right: 8, color: "#fff", fontSize: 22, filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.7))" }} />
                )}
                {isLiked && !isVid && (
                  <FavoriteIcon sx={{ position: "absolute", top: 8, right: 8, fontSize: 16, color: "#EB578A", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }} />
                )}
              </Box>
            );
          })}
        </Box>
      )}

      </Box>{/* end maxWidth container */}

      {/* ── MODAL (fullscreen viewer) ─────────────────────────────────────────── */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{ position: "fixed", inset: 0, bgcolor: "#000", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300, overflow: "hidden" }}
          onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onWheel={handleWheel}
        >
          {/* Close */}
          <IconButton onClick={() => setModalOpen(false)}
            sx={{ position: "fixed", top: "max(env(safe-area-inset-top),14px)", right: 14, zIndex: 20, color: "#fff", bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(8px)", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
            <CloseIcon />
          </IconButton>

          {/* Fullscreen toggle (desktop) */}
          {!isMobile && (
            <IconButton onClick={isFullscreen ? handleExitFullscreen : handleEnterFullscreen}
              sx={{ position: "fixed", top: 14, left: 14, zIndex: 20, color: "#fff", bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(8px)" }}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          )}

          {/* Nav arrows (desktop) */}
          {!isMobile && modalIdx > 0 && (
            <Box onClick={e => { e.stopPropagation(); showPrev(); }}
              sx={{ position: "fixed", left: 16, top: "50%", transform: "translateY(-50%)", zIndex: 20, width: 44, height: 44, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: 24, "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }, userSelect: "none" }}>‹</Box>
          )}
          {!isMobile && modalIdx < images.length - 1 && (
            <Box onClick={e => { e.stopPropagation(); showNext(); }}
              sx={{ position: "fixed", right: 16, top: "50%", transform: "translateY(-50%)", zIndex: 20, width: 44, height: 44, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: 24, "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }, userSelect: "none" }}>›</Box>
          )}

          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              ref={modalRef} key={modalIdx}
              custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit"
              style={{
                width: "100vw", height: "100vh",
                display: "flex", flexDirection: "column",
                background: "#000", position: "relative",
              }}
            >
              {/* Media */}
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {currentImg && (currentIsVideo ? (
                  <video key={currentImg.url} src={currentImg.url} controls autoPlay playsInline
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                ) : (
                  <img src={currentImg.url} alt={currentImg.text || ""}
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                    draggable={false} />
                ))}
              </Box>

              {/* Bottom overlay */}
              <Box sx={{
                position: "absolute", bottom: 0, left: 0, right: 0,
                background: "linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)",
                px: 2, pt: 6, pb: "calc(env(safe-area-inset-bottom) + 80px)", pointerEvents: "none",
              }}>
                {currentImg?.text && (
                  <Typography sx={{ color: "#fff", fontSize: 15, fontWeight: 500, mb: 1, lineHeight: 1.4 }}>
                    {currentImg.text}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pointerEvents: "auto" }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>{modalIdx + 1} / {images.length}</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton onClick={() => handleToggleLike(currentImg)}
                      sx={{ bgcolor: isLiked ? "rgba(235,87,138,0.3)" : "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", color: isLiked ? "#EB578A" : "#fff" }}>
                      {isLiked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    </IconButton>
                    <IconButton onClick={() => handleDownloadItem(currentImg)}
                      sx={{ bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", color: "#fff" }}>
                      <DownloadIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Modal>

      <Snackbar open={toast.open} autoHideDuration={2400} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ borderRadius: 3, fontWeight: 600 }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Gallery;
